import { treatmentFlagValues, TreatmentName } from 'constants/treatments'
import { useEffect, useState } from 'react'
import { getTreatment, isSplitReady } from 'utils/treatment'

/**
 * This hook returns the valid value for a specified treatment. As it takes some time
 * while initializing split client, so until the split client is ready we keep calling
 * the getTreatment function after every 200ms time interval until we get valid value
 * for a particular treatment instead of 'control'.
 *
 * @param treatmentName
 * Required parameter for this hook, pass in the split treatment name.
 *
 * @param allowToFetchTreatment
 * A optional parameter to control when the getTreatment function should be called.
 *
 * @returns returns the treatment value for the specified treatment
 */
const useGetTreatment = (
  name: TreatmentName,
  allowToFetchTreatment = true,
  attributes?: SplitIO.Attributes
): string => {
  const attributesDependency = [...Object.values(attributes ?? {})]
  const [treatmentValue, setTreatmentValue] = useState<string>(
    allowToFetchTreatment
      ? getTreatment(name, attributes)
      : treatmentFlagValues.CONTROL
  )

  const setTreatmentFromSplit = () => {
    const updatedTreatmentValue = getTreatment(name, attributes)
    setTreatmentValue(updatedTreatmentValue)
  }

  /* 
    TODO: Need to find a better way to get the valid value for a treatment instead of
    keep on calling getTreatment function until the Split client is initialized. Check
    with Split.io react SDK (https://github.com/splitio/react-client). This can be used
    later on so that we don't have to handle the split IO implementation ourselves.
  */

  /*
    This is called only for the first time when split is getting ready.
  */
  useEffect(() => {
    let treatmentIntervalHandle

    if (
      allowToFetchTreatment &&
      treatmentValue === treatmentFlagValues.CONTROL
    ) {
      if (isSplitReady()) {
        setTreatmentFromSplit()
      } else {
        treatmentIntervalHandle = setInterval(() => {
          if (isSplitReady()) {
            setTreatmentFromSplit()
            clearInterval(treatmentIntervalHandle)
          }
        }, 100)
      }
    }

    return () => clearInterval(treatmentIntervalHandle)
  }, [allowToFetchTreatment])

  /*
    This useEffect is when any of the attributes change, to invoke the getTreatment function again
  */
  useEffect(() => {
    if (
      allowToFetchTreatment &&
      isSplitReady() &&
      treatmentValue !== treatmentFlagValues.CONTROL
    ) {
      setTreatmentFromSplit()
    }
  }, attributesDependency)

  return treatmentValue
}

export default useGetTreatment
